import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of, Subject, forkJoin } from 'rxjs';
import {
  filter,
  map,
  takeUntil,
  tap,
  finalize,
  shareReplay
} from 'rxjs/operators';
import { AccessGuard } from 'src/app/access/access-guard';
import { EAccess, IAccessMethods } from 'src/app/access/access.model';
import { DEFAULT_URL, HttpMethodType, STATUS } from 'src/app/app.constant';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
  SsoSilentRequest
} from '@azure/msal-browser';
import { IdTokenClaims, PromptValue } from '@azure/msal-common';

import { UserGuard } from '../guards/user.guard';
import { IUserInfo, LoginUser } from '../models/user.model';
import { BASE_URL, HttpCallService } from './http-call.service';
import { apiScopes, b2cPolicies, emailFirstSignIn, loginRequest } from '@app/auth-config';

/** Evide's email-first sign-in page (SSO implementation plan piece 6a). */
export const SIGN_IN_PATH = '/sign-in';

/**
 * How this browser last signed in, so a re-authentication goes the same way. Only the email and the
 * client code (null = the usual password sign-in) - nothing secret. A convenience: if it is lost the
 * person simply sees the email page again.
 */
export interface ISignInRouting {
  email: string;
  providerCode: string | null;
}

const SIGN_IN_ROUTING_KEY = 'evide.signInRouting';
const SIGN_IN_RETURN_URL_KEY = 'evide.signInReturnUrl';

/**
 * Set when a work-login (SSO) user signs out: after B2C ends the Evide session, the email page sends them
 * on to their organisation's (Microsoft's) sign-out, so a shared computer does not keep them signed in
 * there - otherwise the next person pressing Next with the remembered email would get straight in as them.
 */
const ORG_SIGN_OUT_PENDING_KEY = 'evide.orgSignOutPending';
const SIGN_IN_MESSAGE_KEY = 'evide.signInMessage';
// Set when the API refused the token (a sign-out elsewhere, a password reset) and the person is sent to the
// email page: its Next must then ask B2C for the password (prompt=login) even though this browser's B2C
// cookie is still alive - otherwise Log In on Session Expired went straight back in (Aisling, 2026-11-02).
// localStorage, not sessionStorage: the B2C cookie belongs to the whole browser, so closing the tab and
// opening the site in a new one must still ask for the password (found the same day).
const REQUIRE_PASSWORD_KEY = 'evide.requirePassword';
const MICROSOFT_SIGN_OUT_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout';
/**
 * Whether a work-login user's Log out also ends their organisation's Microsoft session (a third hop, to
 * Microsoft's sign-out, which shows a "Pick an account" page because the request cannot name the account
 * without the organisation's login_hint claim). Off since 2026-10-28 (Ronan, Niall: the extra step): Log out
 * ends Evide's and B2C's sessions only, as other Microsoft 365-connected apps do; the organisation's own
 * session rules apply to the rest. Turn on per request - see the SSO plan's follow-ups.
 */
const SIGN_OUT_OF_ORGANISATION = false;

const HttpServiceMethods = {
  Authentication: 'Authentication',
  Logout: 'Authentication/Logout',
  ChangeLicense: 'Authentication/ChangeLicense',
  ChangeProject: 'Authentication/ChangeProject',
  TokenInfo: 'Authentication/TokenInfo',
  CompleteFirstLogin: 'Authentication/CompleteFirstLogin',
  GetPermitedControllers: 'Authentication/GetPermitedControllers',
  GetPermitedControllerMethods: 'Authentication/GetPermitedControllerMethods',
  GetPermitedMultiControllerMethods:
    'Authentication/GetPermitedMultiControllerMethods',
  SignInDiscovery: 'SignInDiscovery/Lookup'
};

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

// B2C writes the user-flow name into acr / tfp in LOWER case ("b2c_1_reset_password") while the
// configured names are "B2C_1_reset_password", so an exact comparison never matched: a completed
// password reset was never recognised (blank page, no session revoke - pen test EVI-003-005).
const isPolicy = (claims: IdTokenClaimsWithPolicyId | undefined | null, policyName: string | null): boolean => {
  if (!policyName) {
    return false;
  }
  const name = policyName.toLowerCase();
  return claims?.acr?.toLowerCase() === name || claims?.tfp?.toLowerCase() === name;
};

/** A sign-in through the organisation sign-in (SSO) policy rather than the usual one. */
const isSsoSignIn = (claims: IdTokenClaimsWithPolicyId | undefined | null): boolean =>
  isPolicy(claims, b2cPolicies.names.ssoSignIn);

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private userKey = 'loginUser';
  private tokenKey = 'access_token';

  private currentUserSubject: BehaviorSubject<LoginUser | null>;
  public currentUserObs: Observable<LoginUser | null>;

  private logoutSubject: Subject<boolean>;
  public logoutObs: Observable<boolean>;

  private isCookieBased = false;

  private readonly _destroying$ = new Subject<void>();
  private loginTriggered = false;

  /** FE-side cache of controller -> methods[] */
  private _methodsCache = new Map<EAccess, string[]>();
  /** Coalesce overlapping requests per controller */
  private _inflightByController = new Map<
    EAccess,
    Observable<IAccessMethods[]>
  >();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private httpCallService: HttpCallService,
    private cookieService: CookieService,
    private httpBackend: HttpBackend
  ) {
    let user = this.storeLoginUser;
    if (!user) {
      this.storeLoginUser = null;
      user = null;
    }

    this.currentUserSubject = new BehaviorSubject<LoginUser | null>(user);
    this.currentUserObs = this.currentUserSubject.asObservable();
    this.logoutSubject = new Subject<boolean>();
    this.logoutObs = this.logoutSubject.asObservable();
  }

  public initiAuth() {
    this.msalService.instance.enableAccountStorageEvents();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0 && !this.loginTriggered) {
          // Re-authenticate in place instead of hard-navigating to the site root.
          // MSAL removes the account when a silent renewal fails with an expired
          // refresh token (invalid_grant) — with the old `window.location.pathname
          // = '/'` the URL being viewed was lost before the login round-trip even
          // started, so B2C returned the user to the root and the router landed
          // them on the dashboard. login() records the current URL as the
          // redirectStartPage, so after the (usually silent, SSO-cookie) round
          // trip the user comes back to the page they were on.
          console.warn('[auth] MSAL account removed/empty — re-authenticating in place');
          this.loginTriggered = true;
          this.login();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (!activeAccount && !this.loginTriggered) {
          this.loginTriggered = true;
          this.login();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        // Back from a completed password reset. Those tokens come from the reset-password user
        // flow, and B2C will only renew a refresh token at the flow that issued it - so keeping
        // this account meant the next silent renewal hit the sign-in endpoint and failed with
        // AADB2C90088, and because checkAndSetActiveAccount could pick it as the active account
        // the user ended up looping or on a blank page. Drop the reset account and sign in
        // through the normal flow (with the new password); never store its token as the session.
        if (
          isPolicy(idtoken, b2cPolicies.names.resetPassword)
        ) {
          const signUpSignInFlowRequest: RedirectRequest | PopupRequest = this.signInRequest({ prompt: PromptValue.LOGIN });
          // Stops the ACCOUNT_REMOVED handler starting a second login while we clear.
          this.loginTriggered = true;
          // A reset from the sign-in page never reaches the API on its own, so every other session
          // (and any refresh token taken from one) carried on - pen test EVI-003-005. B2C always
          // returns here after a completed reset, so end them now, using the reset's own token:
          // the same revoke a sign-out does. Best effort, bounded, then carry on to sign-in.
          console.warn('[auth] password reset completed - ending the other sessions');
          this.revokeAfterPasswordReset(payload.accessToken || payload.idToken)
            .then(() => this.msalService.instance.clearCache({ account: payload.account }))
            .catch(() => undefined)
            .finally(() => this.login(signUpSignInFlowRequest));
          return result;
        }

        this.handleAzureLogin(payload.idToken, payload.account);

        if (
          isPolicy(idtoken, b2cPolicies.names.signUpSignIn) || isSsoSignIn(idtoken)
        ) {
          this.msalService.instance.setActiveAccount(payload.account);
        }

        if (
          isPolicy(idtoken, b2cPolicies.names.editProfile)
        ) {
          const originalSignInAccount = this.msalService.instance
            .getAllAccounts()
            .find(
              (account: AccountInfo) =>
                account.idTokenClaims?.oid === idtoken.oid &&
                account.idTokenClaims?.sub === idtoken.sub &&
                isPolicy(account.idTokenClaims as IdTokenClaimsWithPolicyId, b2cPolicies.names.signUpSignIn)
            );

          const signUpSignInFlowRequest: SsoSilentRequest = {
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            account: originalSignInAccount
          };

          this.msalService.ssoSilent(signUpSignInFlowRequest);
        }

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
            authority: b2cPolicies.authorities.resetPassword.authority,
            scopes: []
          };

          this.login(resetPasswordFlowRequest);
          return;
        }

        // AADB2C90091 = the user pressed Cancel on a B2C self-asserted page (the password
        // reset reached from "Change password", or edit profile). Two things break here, and
        // only the first is visible:
        //
        //  1. MSAL never received a token, so no active account is set and the app renders a
        //     blank page — even though the original signed-in account is still cached.
        //  2. Less obvious: MSAL's cached authority is now the reset-password flow, while the
        //     refresh token it still holds was issued by sign-in. B2C treats every user flow
        //     as a separate issuer, so the next silent renewal posts to the wrong endpoint and
        //     is rejected with AADB2C90088 — logging the user out minutes later, with nothing
        //     on screen to connect it to the Cancel they pressed.
        //
        // Re-entering the sign-in flow rebinds both the account and the authority. The B2C SSO
        // cookie is still valid at this point, so it round-trips without asking for credentials.
        if (result.error && result.error.message.indexOf('AADB2C90091') > -1) {
          if (this.isLoggedIn()) {
            this.login(this.signInRequest());
          } else {
            // Nothing cached to rebind — just land somewhere MsalGuard can act on.
            window.location.href = `/${DEFAULT_URL}`;
          }

          return;
        }

        // AADB2C90157 = too many wrong codes on one B2C page (the authenticator-code step of the custom
        // sign-in policy, pen test EVI-003-002). B2C ends that attempt and returns here with an error;
        // without this the user was left on a blank page. Start a fresh sign-in - the API's attempt
        // counter carries across, so repeated rounds still end in the 30-minute lockout message.
        if (result.error && result.error.message.indexOf('AADB2C90157') > -1) {
          this.login(this.signInRequest());
          return;
        }

        // invalid_grant means the refresh token can no longer be redeemed: expired
        // (AADB2C90080), revoked after a password change (AADB2C90129), or issued by a
        // different user flow (AADB2C90088). MSAL reports the failed renewal and stops there —
        // it never sets an account, so the app renders a blank page and the user has no route
        // back short of clearing site data.
        //
        // The interceptor's "Session Expired" handling can't rescue this: it only fires on a
        // 401 from our own API, and here the failure happens at B2C before any API call is
        // made. Shortening the refresh window to a day makes this a routine end-of-session
        // event rather than an edge case, so it has to land somewhere sensible.
        if (result.error && result.error.message.indexOf('invalid_grant') > -1) {
          this.login(this.signInRequest());
          return;
        }

        // Email first / organisation sign-in: B2C ended the sign-in with a message of ours - typically the
        // API refusing a first work-login link ("There is no Evide account for ..."). Without this the
        // person was left on a blank page. Show the message and go back to the email page.
        // The message is kept across the reload and shown ON the email page: a pop-up here was lost, because
        // the "no account" handling sends the browser to that page (a full load) at the same moment
        // (Niall, dev, 2026-10-28 - testing guide 9.1 showed no message at all).
        if (emailFirstSignIn && result.eventType === EventType.LOGIN_FAILURE && result.error?.message) {
          this.stashSignInMessage(this.describeSignInError(result.error.message));
          this.restartSignIn();
        }
      });
  }

  /**
   * The readable part of a B2C sign-in error: B2C sends "<code>: <message>" followed by correlation lines;
   * keep the message. Falls back to a general sentence.
   */
  private describeSignInError(raw: string): string {
    const firstLine = (raw ?? '').split(/\r?\n/)[0].trim();
    // The API refused to link a work login (B2CConnector/SsoLink answered 409). From an orchestration step
    // B2C does not pass the API's own words on - only "AADB2C90075: The claims exchange 'RestSsoLink' ...
    // returned HTTP error response ..." - so say what that step can mean, with the email the person used.
    if (/SsoLink/i.test(firstLine) || (/AADB2C90075/.test(firstLine) && /HTTP error response/i.test(firstLine))) {
      const email = this.signInRouting?.email;
      return `There is no Evide account for ${email ? email : 'this work login'}, or its licence doesn't allow organisation sign-in. Ask your Evide administrator.`;
    }
    // "access_denied: AADB2C: <our message>" - B2C prefixes the API's own words with a bare "AADB2C:" tag.
    const withoutCode = firstLine.replace(/^[A-Za-z0-9_]+:\s*/, '').replace(/^AADB2C\d*:\s*/, '').trim();
    return withoutCode.length > 0 && withoutCode.length < 300
      ? withoutCode
      : 'Your sign-in could not be completed. Please try again, or contact your Evide administrator.';
  }

  /** A sign-in refusal to show on the email page after the reload that follows a failed B2C sign-in. */
  private stashSignInMessage(message: string): void {
    try {
      sessionStorage.setItem(SIGN_IN_MESSAGE_KEY, message);
    } catch {
      // Without storage the person lands on the email page with no explanation - still not a blank page.
    }
  }

  /** The stashed refusal, once; null when there is none. */
  public takeSignInMessage(): string | null {
    try {
      const message = sessionStorage.getItem(SIGN_IN_MESSAGE_KEY);
      sessionStorage.removeItem(SIGN_IN_MESSAGE_KEY);
      return message;
    } catch {
      return null;
    }
  }

  public destroyAuth() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  /**
   * True when MSAL holds an account, independently of our own stored user.
   * getAllAccounts() throws uninitialized_public_client_application when called
   * before MSAL's initialize() completes — which happens for the first API call
   * at boot — so treat a throw as "not signed in" rather than letting it escape
   * into a caller that only expects a boolean.
   */
  public isLoggedIn(): boolean {
    try {
      return this.msalService.instance.getAllAccounts().length > 0;
    } catch {
      return false;
    }
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    // Email first: a sign-in without a named flow (a first visit, a session that has run out) goes the
    // way this person signed in last time, or to Evide's email page when there is no "last time" or
    // the browser has no signed-in account at all. Named flows (password reset, edit profile, the
    // re-sign-ins above, which already chose their policy) are untouched.
    if (emailFirstSignIn && !userFlowRequest?.authority) {
      if (!this.isLoggedIn() || !this.signInRouting) {
        this.goToSignInPage();
        return;
      }
      userFlowRequest = this.signInRequest(userFlowRequest ?? {});
    }

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.msalService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest
          } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      } else {
        this.msalService
          .loginPopup(userFlowRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      }
    } else {
      // Come back to the page the user is on once the redirect completes. Without
      // an explicit redirectStartPage MSAL uses the href at call time, which is
      // already the site root on some re-auth paths — the user then lands on the
      // dashboard instead of the page they refreshed.
      const redirectStartPage = window.location.href;
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({
          redirectStartPage,
          ...this.msalGuardConfig.authRequest,
          ...userFlowRequest
        } as RedirectRequest);
      } else {
        this.msalService.loginRedirect({
          redirectStartPage,
          ...userFlowRequest
        } as RedirectRequest);
      }
    }
  }

  handleAzureLogin(token: string, account: any) {
    try {
      // Token-rotation path: if someone is already signed in, a silent refresh / SSO
      // event must NOT wipe the FE permission cache (AccessGuard.accessMethods). The
      // full path below clears it and rebuilds it asynchronously, and that transient
      // empty window is what made the licence switcher (and any *accessVisibility
      // element) vanish on idle. Only a genuine sign-in of a *different* account should
      // trigger the full re-init. A silent event often carries a missing/empty account,
      // which previously fell through to the full reset — that's still the same session,
      // so treat it (and an account whose username matches) as a rotation: swap the token.
      const existing = this.currentUser;
      const normUsername = (u?: string) => (u ?? '').trim().toLowerCase();
      const isDifferentAccount =
        !!existing && !!account?.username &&
        normUsername(existing.username) !== normUsername(account.username);
      if (existing && !isDifferentAccount) {
        this.updateToken(token);
        return;
      }

      const previouslyLoadedControllers = Object.keys(AccessGuard.accessMethods) as EAccess[];

      UserGuard.isExecuted = false;
      // reset FE-side permission caches on login
      AccessGuard.accessMethods = {};
      this._methodsCache.clear();
      this._inflightByController.clear();

      this.token = token;
      const currentUser = LoginUser.adaptAzure(token, account);
      this.currentUser = currentUser;

      this.getTokenInfo().subscribe((userInfo) => {
        LoginUser.setUserInfo(this.currentUser, userInfo);
        this.currentUser = currentUser;

        if (previouslyLoadedControllers.length > 0) {
          this.getPermitedMultiControllerMethodsCached(previouslyLoadedControllers).subscribe((res) => {
            res?.forEach((item) => {
              AccessGuard.accessMethods[item.controllerName as EAccess] = item.permitedMethods || [];
            });
            this.currentUser = this.currentUser;
          });
        }
      });
    } catch (e) {
      console.error('error in login', e);
    }
  }

  public checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      return;
    }

    // Prefer the account issued by the sign-in flow. An account left from the reset-password or
    // edit-profile flow can't be renewed at the sign-in endpoint (AADB2C90088), so making it the
    // active account - accounts[0] could be either - ends in a failed renewal and a blank page.
    const isSignInAccount = (account: AccountInfo) => {
      const claims = account.idTokenClaims as IdTokenClaimsWithPolicyId;
      return isPolicy(claims, b2cPolicies.names.signUpSignIn) || isSsoSignIn(claims);
    };
    const signInAccount = accounts.find(isSignInAccount);

    if (!activeAccount) {
      this.msalService.instance.setActiveAccount(signInAccount ?? accounts[0]);
    } else if (!isSignInAccount(activeAccount) && signInAccount) {
      this.msalService.instance.setActiveAccount(signInAccount);
    }
  }

  /**
   * Re-reads the signed-in person's details (TokenInfo) and re-emits the current user, so screens that
   * depend on them - the top bar's licence logo, for one - update without a page load.
   */
  public reloadUserInfo(): void {
    if (!this.currentUser) {
      return;
    }
    this.getTokenInfo().subscribe((info) => {
      LoginUser.setUserInfo(this.currentUser, info);
      this.currentUser = this.currentUser;
    });
  }

  public changeLicense(licenseId: number): Observable<{ status: STATUS }> {
    return this.httpCallService
      .callService(
        HttpMethodType.GET,
        HttpServiceMethods.ChangeLicense + '/' + licenseId
      )
      .pipe(
        tap((res: IUserInfo) => {
          LoginUser.setUserInfo(this.currentUser, res);
          this.currentUser = this.currentUser;
        }),
        map(() => ({ status: STATUS.SUCCESS }))
      );
  }

  public changeProject(projectId: number): Observable<{ status: STATUS }> {
    return this.httpCallService
      .callService(
        HttpMethodType.GET,
        HttpServiceMethods.ChangeProject + `/${projectId}`
      )
      .pipe(
        tap((res) => {
          LoginUser.setUserInfo(this.currentUser, res);
          this.currentUser = this.currentUser;
        }),
        map(() => ({ status: STATUS.SUCCESS }))
      );
  }

  public loginStub(): Observable<{ status: STATUS }> {
    this.token =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ5aWxtYXpzdXBwbGllciIsIlVzZXJOYW1lIjoieWlsbWF6c3VwcGxpZXIiLCJVc2VySWQiOiI5NiIsIkNvbXBhbnlJZCI6IjYwIiwianRpIjoiNGJhMGJhNDUtOGVlMi00YjAyLTgxNDQtNjNkN2YxMDgwMGMzIiwiaHR0cDovL3NjaGVtYXMubWljcm9zb2Z0LmNvbS93cy8yMDA4LzA2L2lkZW50aXR5L2NsYWltcy9yb2xlIjoiU3VwcGxpZXJBZG1pbiIsImV4cCI6MTYyMjI2NjU1NSwiaXNzIjoiWWlsbWF6IFNhcmlkZW1pciIsImF1ZCI6IllpbG1heiBTYXJpZGVtaXIifQ.Yw9CX9TyDgACyaGO658UKFqCMdd0-lzuyfc2-1Z-iyE';
    return of({ status: STATUS.SUCCESS });
  }

  public getTokenInfo(): Observable<IUserInfo> {
    return this.httpCallService.callService(
      HttpMethodType.POST,
      HttpServiceMethods.TokenInfo
    );
  }

  public completeFirstLogin(): Observable<IUserInfo> {
    return this.httpCallService.callService(
      HttpMethodType.POST,
      HttpServiceMethods.CompleteFirstLogin
    );
  }

  private loggingOut = false;

  /** True from the moment the user clicks Log out until the B2C redirect takes over. */
  public get isLoggingOut(): boolean {
    return this.loggingOut;
  }

  public logout() {
    if (this.loggingOut) {
      return;
    }

    // Renew BEFORE flagging the sign-out (acquireBearerToken refuses once loggingOut is set). After
    // the idle timeout the cached tokens are long expired (production issues 10-minute tokens, the
    // idle sign-out comes after an hour), so the revoke went out with the stale stored token, the API
    // answered 401 and nothing was revoked - other sessions and the refresh token carried on (seen in
    // production 2026-10-22: POST Authentication/Logout -> 401). (B2C's own sign-out accepted the
    // expired ID token as the hint - checked on production the same day.) A failed renewal (the B2C
    // session itself has ended) just means there is nothing valid to revoke with; signing out carries on.
    const renewed = this.acquireBearerToken(false).catch(() => undefined);
    this.loggingOut = true;

    // Tell the API to refuse every token already issued to this user, THEN hand over to B2C.
    // B2C ends its own session on sign-out but cannot recall an access token it has already
    // handed out, so without this the token keeps working until it expires — pen test
    // EVI-003-009.
    //
    // Ordering matters twice over. The redirect would cancel the request mid-flight, so it has
    // to go first; and the local session (including the bearer token this very request needs)
    // can only be cleared afterwards, or the call arrives unauthenticated and revokes nothing.
    //
    // Signing out must never hang on it, though: whichever comes first — success, failure, or
    // the timeout — wins, and the hand-over runs exactly once.
    let handedOver = false;
    const handOverToB2C = () => {
      if (handedOver) {
        return;
      }
      handedOver = true;

      // Read the account only now, after the renewal, so the hint below is the fresh ID token.
      const account = this.msalService.instance.getActiveAccount()
        ?? this.msalService.instance.getAllAccounts()[0];

      // The B2C sign-out flow requires the ID token (AADB2C90272 without it). MSAL keeps the raw
      // token on account.idToken - idTokenClaims never had a rawIdToken, so this used to depend on
      // the stored copy in this.token, which is empty after a reset or a cleared session.
      const idTokenHint = account?.idToken ?? this.token ?? undefined;
      this.clearLocalSession();
      if (!idTokenHint) {
        // Nothing B2C would accept: end the session locally and go to sign-in instead of
        // showing B2C's "having trouble signing you in" page.
        this.msalService.instance
          .clearCache()
          .catch(() => undefined)
          .finally(() => this.login({ prompt: PromptValue.LOGIN, scopes: loginRequest.scopes }));
        return;
      }
      // A work-login (SSO) session ends at the policy that started it - B2C only accepts the ID token
      // at the policy that issued it - and then at the organisation too (see ORG_SIGN_OUT_PENDING_KEY).
      const ssoAuthority = this.authorityForAccount(account);
      if (ssoAuthority && SIGN_OUT_OF_ORGANISATION) {
        try {
          sessionStorage.setItem(ORG_SIGN_OUT_PENDING_KEY, '1');
        } catch {
          // Without it the organisation session stays; Evide's own sign-out still completes.
        }
      }
      this.msalService.logoutRedirect({ account, idTokenHint, authority: ssoAuthority });
    };

    // Renewal + revoke usually take well under a second; never let them hold the sign-out longer than this.
    setTimeout(handOverToB2C, 5000);
    renewed
      .then((bearer) => this.revokeSessions(bearer, 'sign-out'))
      .finally(handOverToB2C);
  }

  /**
   * Revokes every session of the user who has just reset their password, with the token the reset
   * flow returned.
   */
  private revokeAfterPasswordReset(bearer: string | undefined): Promise<void> {
    return this.revokeSessions(bearer, 'password reset');
  }

  /**
   * Marks every token issued to this user so far as no longer acceptable to the API (Authentication/
   * Logout - pen test EVI-003-009), with the bearer given. Sent on a bare HttpClient (no interceptors)
   * so the auth interceptor can't swap in a stored token from the session being ended. Best effort:
   * never rejects; gives up after 5 seconds.
   */
  private revokeSessions(bearer: string | undefined, reason: string): Promise<void> {
    if (!bearer) {
      console.warn(`[auth] no valid token to end the sessions with (${reason})`);
      return Promise.resolve();
    }
    const rawClient = new HttpClient(this.httpBackend);
    return new Promise<void>((resolve) => {
      const done = () => resolve();
      const timer = setTimeout(done, 5000);
      rawClient
        .post(BASE_URL + HttpServiceMethods.Logout, {}, { headers: { Authorization: 'Bearer ' + bearer } })
        .subscribe({
          next: () => { clearTimeout(timer); console.warn(`[auth] sessions ended (${reason})`); done(); },
          error: (err) => { clearTimeout(timer); console.warn(`[auth] ending sessions failed (${reason})`, err?.status, err?.error); done(); }
        });
    });
  }

  private clearLocalSession(): void {
    this.logoutSubject.next(true);
    this.currentUser = null;
    this.token = null;
    this.loginTriggered = false;

    // Clear sessionStorage too in case any stale keys remain from the
    // short-lived sessionStorage migration.
    sessionStorage.removeItem(this.userKey);
    sessionStorage.removeItem(this.tokenKey);

    this._methodsCache.clear();
    this._inflightByController.clear();
    AccessGuard.accessMethods = {};
  }

  public set currentUser(user: LoginUser | null) {
    this.storeLoginUser = user;
    this.currentUserSubject.next(user);
  }

  /**
   * Token ROTATION only - the signed-in person has not changed, just the string they hold.
   *
   * Persists without going through the currentUser setter. The setter emits on currentUserObs,
   * and since the interceptor acquires a bearer per request (2026-07-24) this ran on EVERY API
   * call, so every subscriber treated every request as a fresh sign-in: the sidebar re-fetched
   * GetSharingEnabled and rebuilt the whole menu, and the notification, risk-alert and approval
   * feeds each re-polled - four or five extra requests plus several DOM rebuilds per real
   * request. On 2026-09-16 that was 89% of all API traffic (2,507 GetSharingEnabled + 410
   * GetMyNotifications against 18 record opens in twenty minutes) and it is what made record
   * pages stall and render blank on a busy laptop.
   *
   * Subscribers care WHO is signed in, which the sign-in, TokenInfo and licence/project switch
   * paths still emit. Nothing reads the token off the stream: the interceptor and logout read
   * this.token, and `user` is the same object currentUser already holds, so its token field is
   * updated in place for anyone holding a reference.
   */
  public updateToken(newToken: string): void {
    this.token = newToken;
    const user = this.currentUser;
    if (user) {
      user.token = newToken;
      this.storeLoginUser = user;
    }
  }

  private refreshInFlight: Promise<string> | null = null;
  private acquireInFlight: Promise<string> | null = null;

  /**
   * Single silent-acquisition path for the API bearer.
   * - forceRefresh=false: MSAL returns its cached token instantly and only hits
   *   B2C when the token is near expiry — cheap enough to call per request.
   * - forceRefresh=true: bypasses the MSAL cache; used by the 401 retry path,
   *   where the server has already rejected whatever the cache holds.
   * The stored token (this.token / user.token) deliberately remains the ID
   * token — logout() uses it as the idTokenHint B2C requires, and
   * LoginUser.adaptAzure parses its claims. The access token is only ever
   * handed to the Authorization header.
   */
  private acquireBearerToken(forceRefresh: boolean): Promise<string> {
    if (this.loggingOut) {
      return Promise.reject(new Error('Logout in progress'));
    }

    // A force-refresh in flight supersedes everything; a soft acquire may
    // join it (freshest possible token). A force request must NOT join a
    // soft one — the soft result can be the very cached token a 401 just
    // rejected.
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    if (!forceRefresh && this.acquireInFlight) {
      return this.acquireInFlight;
    }

    // Promise.resolve().then(...) so a SYNCHRONOUS MSAL throw becomes a
    // rejection instead of escaping the promise chain. getActiveAccount()
    // throws uninitialized_public_client_application when a request fires
    // before MSAL's initialize() completes — which is exactly what happens
    // to the first API call at boot (terminology load in app.component
    // ngOnInit). A sync throw here would kill the request before dispatch
    // (invisible in the Network tab); as a rejection it flows into
    // getApiToken()'s stored-token fallback and the request proceeds with
    // the pre-existing behaviour.
    const acquisition = Promise.resolve()
      .then(() => {
        const account = this.msalService.instance.getActiveAccount()
          ?? this.msalService.instance.getAllAccounts()[0];

        if (!account) {
          throw new Error('No MSAL account for silent token acquisition');
        }

        // Renew at the policy that issued the sign-in: a work-login (SSO) refresh token is refused at the
        // usual sign-in policy (AADB2C90088), exactly like the password-reset case in initiAuth.
        const silentReq: SilentRequest = {
          ...loginRequest,
          account,
          forceRefresh,
          authority: this.authorityForAccount(account)
        };
        return this.msalService.instance.acquireTokenSilent(silentReq);
      })
      .then((result) => {
        if (result.idToken) {
          this.updateToken(result.idToken);
        }
        return apiScopes.length > 0 && result.accessToken
          ? result.accessToken
          : result.idToken;
      })
      .finally(() => {
        if (forceRefresh) {
          this.refreshInFlight = null;
        } else {
          this.acquireInFlight = null;
        }
      });

    if (forceRefresh) {
      this.refreshInFlight = acquisition;
    } else {
      this.acquireInFlight = acquisition;
    }
    return acquisition;
  }

  /**
   * Proactive per-request bearer. Falls back to the stored token on any MSAL
   * failure so a transient error degrades to the pre-existing behaviour — the
   * interceptor's 401 retry path stays the hard gate for a genuinely dead
   * session.
   */
  public getApiToken(): Promise<string> {
    return this.acquireBearerToken(false).catch(() => {
      const stored = this.token ?? this.currentUser?.token ?? null;
      if (stored) {
        return stored;
      }
      throw new Error('No token available');
    });
  }

  public refreshToken(): Promise<string> {
    return this.acquireBearerToken(true);
  }

  public get currentUser(): LoginUser | null {
    return this.currentUserSubject.value;
  }

  private set storeLoginUser(value: null | LoginUser) {
    if (value === null) {
      localStorage.removeItem(this.userKey);
    } else {
      localStorage.setItem(this.userKey, JSON.stringify(value));
    }
  }

  private get storeLoginUser(): null | LoginUser {
    const userStr = localStorage.getItem(this.userKey);
    if (userStr) {
      try {
        const user: LoginUser = JSON.parse(userStr);
        return user;
      } catch {
        return null;
      }
    }
    return null;
  }

  public set token(value: null | string) {
    if (this.isCookieBased) {
      if (value === null) {
        this.cookieService.delete(this.tokenKey);
      } else {
        this.cookieService.set(this.tokenKey, value);
      }
    } else {
      if (value === null) {
        localStorage.removeItem(this.tokenKey);
      } else {
        localStorage.setItem(this.tokenKey, value);
      }
    }
  }

  public get token(): null | string {
    return this.isCookieBased
      ? this.cookieService.get(this.tokenKey)
      : localStorage.getItem(this.tokenKey);
  }

  public getAccessContollers(): Observable<EAccess[]> {
    return this.httpCallService.callService(
      HttpMethodType.GET,
      HttpServiceMethods.GetPermitedControllers
    );
  }

  public getAccessPermitedMethods(
    controllerName: EAccess
  ): Observable<string[]> {
    return this.httpCallService.callService(
      HttpMethodType.GET,
      HttpServiceMethods.GetPermitedControllerMethods + `/${controllerName}`
    );
  }

  public getPermitedMultiControllerMethods(
    controllersName: EAccess[]
  ): Observable<IAccessMethods[]> {
    return this.httpCallService.callService(
      HttpMethodType.POST,
      HttpServiceMethods.GetPermitedMultiControllerMethods,
      controllersName
    );
  }

  public getPermitedMultiControllerMethodsCached(
    controllersName: EAccess[]
  ): Observable<IAccessMethods[]> {
    if (!controllersName?.length) return of([]);

    const unique = Array.from(new Set(controllersName));
    const pendingInflights: Observable<IAccessMethods[]>[] = [];
    const toFetch: EAccess[] = [];

    for (const c of unique) {
      if (this._methodsCache.has(c)) continue;
      const inflight = this._inflightByController.get(c);
      if (inflight) {
        pendingInflights.push(inflight);
      } else {
        toFetch.push(c);
      }
    }

    if (toFetch.length > 0) {
      const batch$ = this.getPermitedMultiControllerMethods(toFetch).pipe(
        tap((res) => {
          res?.forEach((item) => {
            // Cache POSITIVE answers only. An empty list is treated as "not known yet", not as
            // "this role may call nothing": the server legitimately returns an empty list when a
            // request lands before the session has resolved a role (CurrentRoleId = 0), which
            // happens on the first call after an API restart. Caching that emptied every gated
            // button for the rest of the browser session — no refresh recovered it, only signing
            // out — because nothing ever re-asked. Leaving it uncached costs one extra request
            // for a role that genuinely has no methods on a controller, and self-heals.
            const methods = item.permitedMethods || [];
            if (methods.length) {
              this._methodsCache.set(item.controllerName as EAccess, methods);
            }
          });
        }),
        finalize(() =>
          toFetch.forEach((c) => this._inflightByController.delete(c))
        ),
        shareReplay(1)
      );

      toFetch.forEach((c) => this._inflightByController.set(c, batch$));
      pendingInflights.push(batch$);
    }

    if (pendingInflights.length === 0) {
      return of(
        unique.map((c) => ({
          controllerName: c,
          permitedMethods: this._methodsCache.get(c) || []
        }))
      );
    }

    return forkJoin(pendingInflights).pipe(
      map(() =>
        unique.map((c) => ({
          controllerName: c,
          permitedMethods: this._methodsCache.get(c) || []
        }))
      )
    );
  }

  // ---------------------------------------------------------------------------------------------
  // Email-first sign-in and organisation sign-in (SSO) - implementation plan piece 6a.
  // ---------------------------------------------------------------------------------------------

  /** How this browser last signed in (see ISignInRouting); null when unknown or storage is unavailable. */
  public get signInRouting(): ISignInRouting | null {
    try {
      const raw = localStorage.getItem(SIGN_IN_ROUTING_KEY);
      const parsed = raw ? (JSON.parse(raw) as ISignInRouting) : null;
      return parsed?.email ? parsed : null;
    } catch {
      return null;
    }
  }

  private set signInRouting(value: ISignInRouting | null) {
    try {
      if (value) {
        localStorage.setItem(SIGN_IN_ROUTING_KEY, JSON.stringify(value));
      } else {
        localStorage.removeItem(SIGN_IN_ROUTING_KEY);
      }
    } catch {
      // Storage blocked or full: email first still works, it just won't remember the email.
    }
  }

  /** The SSO policy's authority for a work-login account; undefined (= the configured default) otherwise. */
  private authorityForAccount(account: AccountInfo | null | undefined): string | undefined {
    const claims = account?.idTokenClaims as IdTokenClaimsWithPolicyId | undefined;
    return isSsoSignIn(claims) ? b2cPolicies.authorities.ssoSignIn.authority ?? undefined : undefined;
  }

  /**
   * A sign-in request that goes the way this person signed in last time: their organisation's login
   * (SSO policy + domain_hint) or the usual policy, with their email filled in either way.
   */
  private signInRequest(extra: Partial<RedirectRequest> = {}): RedirectRequest {
    const routing = emailFirstSignIn ? this.signInRouting : null;
    const ssoAuthority = b2cPolicies.authorities.ssoSignIn.authority;
    if (routing?.providerCode && ssoAuthority) {
      return {
        scopes: loginRequest.scopes,
        authority: ssoAuthority,
        loginHint: routing.email,
        extraQueryParameters: { domain_hint: routing.providerCode },
        ...extra
      };
    }
    return {
      scopes: loginRequest.scopes,
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      ...(routing?.email ? { loginHint: routing.email } : {}),
      ...extra
    };
  }

  /** Remembers where to come back to, then shows Evide's email page (a full load, so MSAL starts clean). */
  public goToSignInPage(returnUrl: string = window.location.href): void {
    if (window.location.pathname.startsWith(SIGN_IN_PATH)) {
      return;
    }
    try {
      sessionStorage.setItem(SIGN_IN_RETURN_URL_KEY, returnUrl);
    } catch {
      // Without it the person lands on the dashboard after signing in.
    }
    window.location.assign(SIGN_IN_PATH);
  }

  /** Called by the root guard, which sends a signed-out visitor to the email page without a reload. */
  public rememberReturnUrl(url: string): void {
    try {
      sessionStorage.setItem(SIGN_IN_RETURN_URL_KEY, new URL(url, window.location.origin).href);
    } catch {
      // As above.
    }
  }

  /** The page to return to after signing in (only on this site), else the site root. */
  private takeReturnUrl(): string {
    let url: string | null = null;
    try {
      url = sessionStorage.getItem(SIGN_IN_RETURN_URL_KEY);
      sessionStorage.removeItem(SIGN_IN_RETURN_URL_KEY);
    } catch {
      url = null;
    }
    const origin = window.location.origin;
    return url && url.startsWith(origin) && !new URL(url).pathname.startsWith(SIGN_IN_PATH) ? url : `${origin}/`;
  }

  /**
   * Email page -> Next: asks the API which sign-in this email uses, remembers it, and starts that
   * sign-in - the organisation's login for an SSO licence's domain, else the usual password page -
   * with the email filled in.
   */
  public signInWithEmail(email: string): Observable<void> {
    const trimmed = email.trim();
    return this.httpCallService
      .callService(HttpMethodType.POST, HttpServiceMethods.SignInDiscovery, { email: trimmed })
      .pipe(
        map((res: { sso?: boolean; providerCode?: string | null }) => {
          const providerCode = res?.sso && res.providerCode && b2cPolicies.authorities.ssoSignIn.authority
            ? res.providerCode
            : null;
          this.signInRouting = { email: trimmed, providerCode };
          this.msalService.loginRedirect(this.signInRequest({
            redirectStartPage: this.takeReturnUrl(),
            ...(this.takeRequirePassword() ? { prompt: PromptValue.LOGIN } : {})
          }));
        })
      );
  }

  /**
   * After a work-login user's sign-out has ended the Evide session: sends the browser on to Microsoft's
   * sign-out, which ends their organisation session here. Returns true when it has done so.
   */
  public continueOrganisationSignOut(): boolean {
    let pending = false;
    try {
      pending = sessionStorage.getItem(ORG_SIGN_OUT_PENDING_KEY) === '1';
      sessionStorage.removeItem(ORG_SIGN_OUT_PENDING_KEY);
    } catch {
      pending = false;
    }
    if (pending) {
      window.location.assign(MICROSOFT_SIGN_OUT_URL);
    }
    return pending;
  }

  /** Whether this sign-in must ask for the password (see REQUIRE_PASSWORD_KEY); read once. */
  private takeRequirePassword(): boolean {
    try {
      const required = localStorage.getItem(REQUIRE_PASSWORD_KEY) === '1';
      localStorage.removeItem(REQUIRE_PASSWORD_KEY);
      return required;
    } catch {
      return false;
    }
  }

  /** "Not you?" on the email page: forget the remembered email. */
  public forgetSignInEmail(): void {
    this.signInRouting = null;
  }

  /**
   * Start again from nothing: drop this browser's sign-in (the stored user AND MSAL's cached account), then
   * go to the email page when email-first sign-in is on, else straight to B2C (prompt=login when the API has
   * refused the token - a sign-out elsewhere, a password reset - so a shared browser's B2C cookie cannot ride
   * straight back in). Clearing the cache is the point: a revoked sign-in left in the cache raised the
   * Session Expired box again on every visit until a sign-in was completed (Niall, dev, 2026-10-29).
   */
  public restartSignIn(requirePassword = true): void {
    this.loginTriggered = true;
    this.clearLocalSession();
    this.msalService.instance
      .clearCache()
      .catch(() => undefined)
      .finally(() => {
        if (emailFirstSignIn) {
          if (requirePassword) {
            try {
              localStorage.setItem(REQUIRE_PASSWORD_KEY, '1');
            } catch {
              // Without it the B2C cookie may sign the person back in without a password.
            }
          }
          this.goToSignInPage(`${window.location.origin}/`);
        } else {
          this.loginTriggered = false;
          this.login(requirePassword ? { prompt: PromptValue.LOGIN, scopes: loginRequest.scopes } : undefined);
        }
      });
  }

  editProfile() {
    const editProfileFlowRequest: RedirectRequest | PopupRequest = {
      authority: b2cPolicies.authorities.editProfile.authority,
      scopes: []
    };
    this.login(editProfileFlowRequest);
  }

  /**
   * Send the user into the B2C password-reset flow on demand.
   *
   * The flow was previously reachable only reactively — via the login page's "Forgot
   * password?" link (B2C error AADB2C90118) — so a signed-in user had no way to change a
   * password they suspected was compromised. Pen test EVI-003-013.
   *
   * Note this verifies identity by emailed code rather than by asking for the current
   * password; that is what the B2C reset flow does and it is not configurable here.
   */
  changePassword() {
    const passwordResetFlowRequest: RedirectRequest | PopupRequest = {
      authority: b2cPolicies.authorities.resetPassword.authority,
      scopes: []
    };
    this.login(passwordResetFlowRequest);
  }
}
